import styles from "./styles.module.scss";
import { Input, Spin, Button, Radio, Space } from "antd";
import { useStarRating } from "./useHook";
import {
  appPerformanceSurveyController,
  localStorageProvider,
  supportController,
  analyticsController,
  engagementTrackerProvider,
} from "../../../AppController";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { isVitalsSubdomain } from "../../../util/subdomain";

const StarRatingPrompt = () => {
  const {
    isSurveyOpen,
    currentStep,
    selectedRating,
    note,
    onRatingClick,
    onNoteChange,
    onCommentSubmit,
    onFinalSubmit,
    onClose,
    showSuccessModal,
    additionalAnswer,
    setAdditionalAnswer,
    isLoading,
  } = useStarRating(
    appPerformanceSurveyController,
    localStorageProvider,
    supportController,
    analyticsController,
    engagementTrackerProvider
  );

  const [isFloatingSheetOpen, setIsFloatingSheetOpen] = useState(false);

  // Detect if FloatingSheet or FloatingSheetForFlagSummay is open
  useEffect(() => {
    const checkFloatingSheet = () => {
      const floatingSheetOpen = document.querySelector(".floatingSheetOpen");
      const floatingPanel = document.querySelector(".adm-floating-panel");
      setIsFloatingSheetOpen(!!(floatingSheetOpen || floatingPanel));
    };

    // Initial check
    checkFloatingSheet();

    // Set up a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(checkFloatingSheet);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Disable star rating on vitals subdomain
  if (isVitalsSubdomain()) return null;

  // Disable star rating on VitalSync page (regardless of subdomain)
  if (window.location.href.includes("/#/vitals-ai")) return null;

  if (!isSurveyOpen) return null;

  const stars = [1, 2, 3, 4, 5];

  // Success Modal
  if (showSuccessModal) {
    return (
      <div
        className={`starRatingPopupOuterContainer ${styles.popupContainer} ${
          isFloatingSheetOpen ? styles.popupContainerFloatingSheetOpen : ""
        }`}
      >
        <div className={styles.successModal}>
          <div className={styles.successIcon}>
            <CheckCircleOutlined />
          </div>
          <div className={styles.successTitle}>
            Thank you for your feedback!
          </div>
          <div className={styles.successSubtitle}>
            We'll use it to keep improving Circadia Intelligence Platform
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Rating selection
  if (currentStep === 1) {
    return (
      <Spin className="starRatingPopupOuterContainer" spinning={isLoading}>
        <div
          className={`${styles.popupContainer} ${
            isFloatingSheetOpen ? styles.popupContainerFloatingSheetOpen : ""
          }`}
        >
          <div className={styles.surveyContainer}>
            <button className={styles.closeButton} onClick={onClose}>
              <CloseOutlined />
            </button>
            <div className={styles.title}>
              👋 Hi there. To what extent do you agree or disagree that Circadia
              Intelligence Platform effectively supports the work you do?
            </div>

            <div className={styles.starsContainer}>
              {stars.map((star) => (
                <div
                  key={`star-${star}`}
                  className={`${styles.starWrapper} ${
                    selectedRating === star ? styles.starWrapperSelected : ""
                  } 
                  ${star === 5 ? styles.rightStarBorderRadius : ""}
                  ${star === 1 ? styles.leftStarBorderRadius : ""}
                  `}
                  onClick={() => onRatingClick(star)}
                >
                  <span className={styles.starNumber}>{star}</span>
                </div>
              ))}
            </div>

            <div className={styles.starLabels}>
              <span className={styles.labelLeft}>Strongly disagree</span>
              <span className={styles.labelRight}>Strongly agree</span>
            </div>
          </div>
        </div>
      </Spin>
    );
  }

  // Step 2: Comment question
  if (currentStep === 2) {
    return (
      <Spin className="starRatingPopupOuterContainer" spinning={isLoading}>
        <div
          className={`${styles.popupContainer} ${
            isFloatingSheetOpen ? styles.popupContainerFloatingSheetOpen : ""
          }`}
        >
          <div className={styles.surveyContainer}>
            <button className={styles.closeButton} onClick={onClose}>
              <CloseOutlined />
            </button>
            <div className={styles.title}>
              What would make Circadia Intelligence Platform even better for
              you?
            </div>

            <Input.TextArea
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="I think that..."
              className={styles.textArea}
              rows={4}
              autoFocus
            />

            <div
              className={styles.buttonContainer}
              style={{ marginTop: "24px" }}
            >
              <Button
                onClick={onCommentSubmit}
                type="primary"
                className="circadiaButton"
                size="large"
                block
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </Spin>
    );
  }

  // Step 3: Additional question
  if (currentStep === 3) {
    return (
      <Spin className="starRatingPopupOuterContainer" spinning={isLoading}>
        <div
          className={`${styles.popupContainer} ${
            isFloatingSheetOpen ? styles.popupContainerFloatingSheetOpen : ""
          }`}
        >
          <div className={styles.surveyContainer}>
            <button className={styles.closeButton} onClick={onClose}>
              <CloseOutlined />
            </button>
            <div className={styles.titleFollowUpQuestion}>
              How would you feel if you could no longer use Circadia
              Intelligence Platform?
            </div>

            <Radio.Group
              onChange={(e) => setAdditionalAnswer(e.target.value)}
              value={additionalAnswer}
              className={styles.radioGroup}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Radio value="Very disappointed">Very disappointed</Radio>
                <Radio value="Somewhat disappointed">
                  Somewhat disappointed
                </Radio>
                <Radio value="Not disappointed">Not disappointed</Radio>
              </Space>
            </Radio.Group>

            <div className={styles.buttonContainer}>
              <Button
                onClick={onFinalSubmit}
                type="primary"
                className="circadiaButton"
                size="large"
                block
                disabled={!additionalAnswer}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      </Spin>
    );
  }

  return null;
};

export default StarRatingPrompt;
